import { useCallback, useEffect, useState } from 'react';
import { savePreference } from './preferenceSync';

// Recently Deleted retention policy (#127). The user picks how long a deleted
// document lingers in the "Recently Deleted" trash before it is permanently
// purged: "immediately" (no trash — delete destroys), 7 days, or 30 days.
// Server-persisted in the profile since #480 (synced across devices), cached in
// localStorage and broadcast to open views via a window event. The numeric value (days) drives both the auto-purge the
// File Manager requests on opening the trash AND whether a delete routes to
// trash vs. a hard delete.

const STORAGE_KEY = 'omnis:retention-policy';
export const RETENTION_OPTIONS = [
    { value: 'immediately', label: 'Immediately', days: 0 },
    { value: '7', label: '7 days', days: 7 },
    { value: '30', label: '30 days', days: 30 },
];
const DEFAULT_POLICY = '30';

const VALID = new Set(RETENTION_OPTIONS.map((o) => o.value));

export function readRetentionPolicy() {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        if (v && VALID.has(v)) return v;
    } catch (_) { /* ignore */ }
    return DEFAULT_POLICY;
}

// Days for the active policy, or null when "immediately" (no trash staging).
export function retentionDays(policy) {
    const p = policy || readRetentionPolicy();
    if (p === 'immediately') return 0;
    const n = parseInt(p, 10);
    return Number.isFinite(n) ? n : 30;
}

export function isImmediatePolicy(policy) {
    return (policy || readRetentionPolicy()) === 'immediately';
}

export function useRetentionPolicy() {
    const [policy, setPolicyState] = useState(readRetentionPolicy);

    useEffect(() => {
        const onChange = (e) => {
            const next = e?.detail?.policy || readRetentionPolicy();
            setPolicyState(next);
        };
        const onStorage = (e) => { if (e.key === STORAGE_KEY) setPolicyState(readRetentionPolicy()); };
        window.addEventListener('omnis:retention-changed', onChange);
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener('omnis:retention-changed', onChange);
            window.removeEventListener('storage', onStorage);
        };
    }, []);

    const setPolicy = useCallback((next) => {
        const clean = VALID.has(next) ? next : DEFAULT_POLICY;
        setPolicyState(clean);
        return savePreference('retention_policy', clean);
    }, []);

    return { policy, setPolicy, options: RETENTION_OPTIONS };
}

export default useRetentionPolicy;
